import { SearchModes } from '@/domainLogic/index.ts';
import type { Filter, FiltersGroup, FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { capitalize } from '@/utils/stringUtils.ts';
import { hasAirportsOnly } from '@/app/components/search/utils/productsResponse.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import {
  evoFiltersEnabled,
  freeCancellationWithdrawn,
  newFacilitiesEnabled,
} from '@/app/common/filters/core/filterFlags.ts';
import { OPTIONS_SORT } from '@/app/common/filters/types/shared/utils/sort.ts';
import { shouldShowPropertyNameFilter } from '@/app/components/search/utils/api/isLiveSearchByDate.ts';
import {
  createShouldShowDestinationAirportFilter,
  createShouldShowOvernightFlightsFilter,
} from '@/app/common/filters/groups/visibility.ts';
import { createPropertyNameFilters } from '@/app/common/filters/groups/createPropertyNameFilters.ts';
import { createFacilitiesFilters } from '@/app/common/filters/groups/createFacilitiesFilters.ts';
import { createFacilityGroupFilters } from '@/app/common/filters/groups/createFacilityGroupFilters.ts';
import {
  ACCOMMODATION_FACILITY_FILTER_NAMES,
  FACILITY_GROUP_FILTER_NAMES,
} from '@/app/common/filters/groups/facilityGroups.ts';
import { createDiscountFilters } from '@/app/common/filters/groups/createDiscountFilters.ts';
import { createBookingOptionsFilters } from '@/app/common/filters/groups/createBookingOptionsFilters.ts';
import { createDestinationAirportFilters } from '@/app/common/filters/groups/createDestinationAirportFilters.ts';
import { createOvernightFlightsFilters } from '@/app/common/filters/groups/createOvernightFlightsFilters.ts';
import { createPriceFilters } from '@/app/common/filters/groups/createPriceFilters.ts';
import { createFlightsFilters } from '@/app/common/filters/groups/createFlightFilters.ts';
import { createPropertyTypeFilters } from '@/app/common/filters/groups/createPropertyTypeFilters.ts';
import { createBlueFlagFilters } from '@/app/common/filters/groups/createBlueFlagFilters.ts';
import { createBeachTypeFilters } from '@/app/common/filters/groups/createBeachTypeFilters.ts';
import { createClimateFilters } from '@/app/common/filters/groups/createClimateFilters.ts';
import { createFaunaFilters } from '@/app/common/filters/groups/createFaunaFilters.ts';
import { createWaterSportsFilters } from '@/app/common/filters/groups/createWatersportsFilters.ts';
import { createEntertainmentSportsFilters } from '@/app/common/filters/groups/createEntertainmentFilters.ts';
import { createWaterWavesSportsFilters } from '@/app/common/filters/groups/createWaterWavesFilters.ts';
import { createLeisureTypeFilters } from '@/app/common/filters/groups/createLeisureTypeFilters.ts';
import { createStarsFilters } from '@/app/common/filters/groups/createStarsFilters.tsx';
import { createMealPlansFilters } from '@/app/common/filters/groups/createMealPlansFilters.ts';
import { createRatingFilters } from '@/app/common/filters/groups/createRatingFilters.tsx';
import { createPropertyStyleFilters } from '@/app/common/filters/groups/createPropertyStyleFilters.ts';
import { createSortFilters } from '@/app/common/filters/groups/createSortFilters.ts';
import { createSustainabilityCertificateFilters } from '@/app/common/filters/groups/createSustainabilityCertificateFilters.ts';
import { createSearchFilterWithLabel } from '@/app/common/filters/groups/filterGroupsUtils.ts';
import { SUSTAINABILITY_CERTIFICATE_COPY } from '@/app/common/sustainabilityCertificate.ts';

export type FilterGroupContext = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  loggedIn: boolean;
  commonData: CommonData;
  localization: Localization;
  productsData: ProductsData;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};

export type FilterAvailabilityContext = Pick<FilterGroupContext, 'searchParams' | 'localization' | 'searchInfoData'>;

type FilterGroupShape<Context> = {
  id: FiltersGroupId;
  label: (context: Context) => string;
  createFilters: (context: Context) => Filter[];
  visibility?: (group: FiltersGroup, context: Context) => boolean;
  showMore?: boolean;
  // Names withdrawn while the group itself stays available.
  unavailable?: (context: FilterAvailabilityContext) => FILTER_NAME[];
};

type Unconditional = { available?: never; filterNames?: never };

type Conditional = {
  available: (context: FilterAvailabilityContext) => boolean;
  filterNames: FILTER_NAME[];
};

export type FilterGroupDescriptor<Context = FilterGroupContext> = FilterGroupShape<Context> &
  (Unconditional | Conditional);

const notUnderApex = ({ localization }: FilterAvailabilityContext) =>
  !localization.configValuesApp.apexEnabledForResults;

// MPH-1034: cux_4082 variant T withdraws free cancellation only; book now pay later stays.
const withdrawnBookingOptions = ({ localization }: FilterAvailabilityContext): FILTER_NAME[] =>
  freeCancellationWithdrawn(localization.configValuesApp) ? [FILTER_NAME.FREE_CANCELLATION] : [];

const inactiveFacilityFilters = ({ localization }: FilterAvailabilityContext): FILTER_NAME[] =>
  newFacilitiesEnabled(localization.configValuesApp)
    ? ACCOMMODATION_FACILITY_FILTER_NAMES
    : FACILITY_GROUP_FILTER_NAMES;

const translated =
  (key: string) =>
  ({ translationDictionary }: FilterGroupContext) =>
    getTranslationServer(key, translationDictionary);

type AccommodationSearchGroup = {
  id: FILTER_NAME;
  titleKey: string;
  placeholderKey: string;
  anyLabelKey?: string;
  urlParameterName: URLParameters;
  dynamicData: (context: FilterGroupContext) => Parameters<typeof createSearchFilterWithLabel>[0]['dynamicData'];
};

const NO_MATCHES_KEY = 'vacation.oss.filters.districts.no_matches';

const accommodationSearchGroup = ({
  id,
  titleKey,
  placeholderKey,
  anyLabelKey,
  urlParameterName,
  dynamicData,
}: AccommodationSearchGroup): FilterGroupDescriptor => ({
  id,
  label: translated(titleKey),
  createFilters: (context) =>
    createSearchFilterWithLabel({
      filterName: id,
      searchParams: context.searchParams,
      urlParameterName,
      dynamicData: dynamicData(context),
      placeholder: translated(placeholderKey)(context),
      ...(anyLabelKey && { anyLabel: translated(anyLabelKey)(context) }),
      noResultsLabel: translated(NO_MATCHES_KEY)(context),
      optionsSort: OPTIONS_SORT.ALPHABET,
    }),
});

export const FILTER_GROUPS: FilterGroupDescriptor[] = [
  {
    id: CUSTOM_GROUP_NAME.DISCOUNT_OPTIONS,
    label: translated('vacation.oss.filters.discounts'),
    createFilters: createDiscountFilters,
  },
  {
    id: CUSTOM_GROUP_NAME.BOOKING_OPTIONS,
    label: translated('vacation.oss.filters.booking_options.title'),
    createFilters: createBookingOptionsFilters,
    available: notUnderApex,
    filterNames: [FILTER_NAME.FREE_CANCELLATION, FILTER_NAME.BOOK_NOW_PAY_LATER],
    unavailable: withdrawnBookingOptions,
    // A group with no filters is not a group.
    visibility: (filtersGroup) => filtersGroup.filters.length > 0,
  },
  {
    id: FILTER_NAME.DESTINATION_AIRPORT,
    label: (context) =>
      hasAirportsOnly(context.productsData)
        ? translated('vacation.oss.filters.destination_airport.title')(context)
        : translated('vacation.oss.filters.destination_airport_station.title')(context),
    createFilters: createDestinationAirportFilters,
    available: notUnderApex,
    filterNames: [FILTER_NAME.DESTINATION_AIRPORT],
    visibility: (filtersGroup) => createShouldShowDestinationAirportFilter({ filtersGroup }),
  },
  {
    id: FILTER_NAME.OVERNIGHT_FLIGHTS,
    label: (context) =>
      context.searchInfoData.searchMode === SearchModes.TH
        ? translated('vacation.oss.overnight_train.filter.label')(context)
        : translated('vacation.oss.overnight_flight.filter.label')(context),
    createFilters: createOvernightFlightsFilters,
    available: notUnderApex,
    filterNames: [FILTER_NAME.OVERNIGHT_FLIGHTS],
    visibility: (filtersGroup) => createShouldShowOvernightFlightsFilter({ filtersGroup }),
  },
  {
    id: FILTER_NAME.PRICE_RANGE,
    label: translated('vacation.oss.filters.budget.title'),
    createFilters: createPriceFilters,
  },
  {
    id: FILTER_NAME.DIRECT_FLIGHT,
    label: (context) => capitalize(translated('vacation.oss.components.searchform.flight')(context)),
    createFilters: createFlightsFilters,
    available: notUnderApex,
    filterNames: [FILTER_NAME.DIRECT_FLIGHT],
  },
  {
    id: FILTER_NAME.SUSTAINABILITY_CERTIFICATE,
    label: translated(SUSTAINABILITY_CERTIFICATE_COPY.filterTitle),
    // Same two-flag rule as the detail-page certifications: a market with sustainability off
    // never shows certifications either.
    available: ({ localization }) =>
      !!localization.configValuesApp.sustainabilityEnabled &&
      !!localization.configValuesApp.sustainabilityCertificationsEnabled,
    filterNames: [FILTER_NAME.SUSTAINABILITY_CERTIFICATE],
    createFilters: createSustainabilityCertificateFilters,
  },
  {
    id: FILTER_NAME.PROPERTY_TYPE,
    label: translated('vacation.search.filter.accommodation.type.title'),
    createFilters: ({ searchParams, dynamicFiltersResponse, translationDictionary }) =>
      createPropertyTypeFilters({
        searchParams,
        dynamicFiltersResponse,
        translationDictionary,
        optionsSort: OPTIONS_SORT.NUM_OR_NONE,
      }),
  },
  {
    id: FILTER_NAME.BLUE_FLAG,
    label: translated('vacation.oss.filters.blue_flag.title'),
    createFilters: createBlueFlagFilters,
  },
  {
    id: FILTER_NAME.BEACH_TYPE,
    label: translated('vacation.oss.filters.beach_type.title'),
    createFilters: createBeachTypeFilters,
  },
  {
    id: FILTER_NAME.CLIMATE,
    label: translated('vacation.oss.filters.climate.title'),
    createFilters: createClimateFilters,
  },
  {
    id: FILTER_NAME.FAUNA,
    label: translated('vacation.oss.filters.fauna.title'),
    createFilters: createFaunaFilters,
  },
  {
    id: FILTER_NAME.WATER_SPORTS,
    label: translated('vacation.oss.filters.watersport.title'),
    createFilters: createWaterSportsFilters,
  },
  {
    id: FILTER_NAME.ENTERTAINMENT,
    label: translated('vacation.oss.filters.entertainments.title'),
    createFilters: createEntertainmentSportsFilters,
  },
  {
    id: FILTER_NAME.WATER_WAVES,
    label: translated('vacation.oss.filters.waterwaves.title'),
    createFilters: createWaterWavesSportsFilters,
  },
  {
    id: FILTER_NAME.PROPERTY_NAME,
    label: translated('vacation.oss.filters.property.title'),
    createFilters: createPropertyNameFilters,
    available: ({ searchParams }) => shouldShowPropertyNameFilter(searchParams),
    filterNames: [FILTER_NAME.PROPERTY_NAME],
  },
  {
    id: FILTER_NAME.LEISURE_TYPE,
    label: translated('vacation.oss.filters.leisure.title'),
    createFilters: createLeisureTypeFilters,
  },
  {
    id: FILTER_NAME.STARS,
    label: ({ localization, translationDictionary }) =>
      getTranslationServer(
        evoFiltersEnabled(localization?.configValuesApp)
          ? 'vacation.oss.filters.stars.title_long'
          : 'vacation.oss.filters.stars.title',
        translationDictionary
      ),
    createFilters: ({ searchParams, dynamicFiltersResponse, localization, translationDictionary }) =>
      createStarsFilters({
        searchParams,
        valueList: dynamicFiltersResponse?.accommodationCategory,
        useEvoFilters: evoFiltersEnabled(localization?.configValuesApp),
        noRatingLabel: getTranslationServer('vacation.oss.filters.stars.no_rating', translationDictionary),
      }),
  },
  {
    id: FILTER_NAME.MEAL_PLAN,
    label: translated('vacation.oss.components.searchfilters.group.meal'),
    createFilters: createMealPlansFilters,
  },
  {
    id: FILTER_NAME.RATING,
    label: translated('vacation.oss.components.searchfilters.group.rating'),
    createFilters: ({ searchParams, dynamicFiltersResponse, localization, translationDictionary, productsData }) =>
      createRatingFilters({
        searchParams,
        dynamicFiltersResponse,
        translationDictionary,
        productsData,
        useEvoFilters: evoFiltersEnabled(localization?.configValuesApp),
      }),
  },
  {
    id: CUSTOM_GROUP_NAME.FACILITIES,
    label: translated('vacation.search.filters.facilities.title'),
    createFilters: ({ searchParams, dynamicFiltersResponse, localization, translationDictionary }) =>
      newFacilitiesEnabled(localization?.configValuesApp)
        ? createFacilityGroupFilters({
            searchParams,
            facilities: dynamicFiltersResponse?.facilities,
            translationDictionary,
          })
        : createFacilitiesFilters({ searchParams, dynamicFiltersResponse, translationDictionary }),
    unavailable: inactiveFacilityFilters,
    showMore: true,
  },
  accommodationSearchGroup({
    id: FILTER_NAME.ACCOMMODATION_BEACH,
    titleKey: 'vacation.oss.filters.beach.title',
    placeholderKey: 'vacation.oss.filters.beach.placeholder',
    anyLabelKey: 'vacation.oss.filters.beach.any',
    urlParameterName: URLParameters.ACCOMODATION_BEACH,
    dynamicData: ({ dynamicFiltersResponse }) => dynamicFiltersResponse?.accomodationBeaches,
  }),
  accommodationSearchGroup({
    id: FILTER_NAME.ACCOMMODATION_SKI_LIFT,
    titleKey: 'vacation.oss.filters.skilift.title',
    placeholderKey: 'vacation.oss.filters.skilift.placeholder',
    anyLabelKey: 'vacation.oss.filters.skilift.any',
    urlParameterName: URLParameters.ACCOMODATION_SKILIFT,
    dynamicData: ({ dynamicFiltersResponse }) => dynamicFiltersResponse?.accomodationSkilifts,
  }),
  accommodationSearchGroup({
    id: FILTER_NAME.ACCOMMODATION_DISTRICT,
    titleKey: 'vacation.oss.filters.districts.title',
    placeholderKey: 'vacation.oss.filters.districts.placeholder',
    urlParameterName: URLParameters.ACCOMODATION_DISTRICT,
    dynamicData: ({ dynamicFiltersResponse }) => dynamicFiltersResponse?.accommodationDistricts,
  }),
  accommodationSearchGroup({
    id: FILTER_NAME.ACCOMMODATION_STATION,
    titleKey: 'vacation.oss.filters.stations.title',
    placeholderKey: 'vacation.oss.filters.stations.placeholder',
    anyLabelKey: 'vacation.oss.filters.stations.any',
    urlParameterName: URLParameters.ACCOMODATION_STATION,
    dynamicData: ({ dynamicFiltersResponse }) => dynamicFiltersResponse?.accommodationStations,
  }),
  accommodationSearchGroup({
    id: FILTER_NAME.ACCOMMODATION_CHAIN,
    titleKey: 'vacation.oss.filters.hotel_chain.title',
    placeholderKey: 'vacation.oss.filters.hotel_chain.placeholder',
    urlParameterName: URLParameters.ACCOMODATION_CHAIN,
    dynamicData: ({ dynamicFiltersResponse }) => dynamicFiltersResponse?.accommodationChains,
  }),
  {
    id: FILTER_NAME.PROPERTY_STYLE,
    label: translated('vacation.oss.filters.hotel_theme.title'),
    createFilters: ({ searchParams, dynamicFiltersResponse, translationDictionary }) =>
      createPropertyStyleFilters({
        searchParams,
        dynamicFiltersResponse,
        translationDictionary,
        optionsSort: OPTIONS_SORT.AUTO,
      }),
  },
  {
    id: FILTER_NAME.SORT,
    label: translated('vacation.oss.components.searchresults.sort.title'),
    createFilters: createSortFilters,
  },
];
